import z from "zod";
import type { Product } from "@/lib/products";

/**
 * The storefront catalogue's filters live in the URL, so a filtered page can be
 * shared, bookmarked and walked back through with the browser's back button.
 */

export const StorefrontSorts = {
  featured: "featured",
  newest: "newest",
  priceAsc: "price_asc",
  priceDesc: "price_desc",
  nameAsc: "name_asc",
} as const;

export type StorefrontSort =
  (typeof StorefrontSorts)[keyof typeof StorefrontSorts];

const sortValues = Object.values(StorefrontSorts) as [
  StorefrontSort,
  ...StorefrontSort[],
];

// Every field falls back to "unset" on a malformed value, so a hand-edited or
// outdated link still opens the catalogue instead of an error page.
export const storefrontSearchSchema = z.object({
  q: z.string().trim().max(100).optional().catch(undefined),
  category: z.string().max(64).optional().catch(undefined),
  tags: z.array(z.string().max(64)).max(50).optional().catch(undefined),
  min: z.number().nonnegative().optional().catch(undefined),
  max: z.number().nonnegative().optional().catch(undefined),
  inStock: z.boolean().optional().catch(undefined),
  sort: z.enum(sortValues).optional().catch(undefined),
});

export type StorefrontSearch = z.infer<typeof storefrontSearchSchema>;

interface CatalogCategory {
  id: string;
  name: string;
  position: number;
  products: Product[];
}

/** The price a product is listed at: its own, or its cheapest variant. */
export function getListPrice(product: Product): number | undefined {
  const variantPrices = (product.variants ?? [])
    .map((variant) => variant.deliveryPrice ?? variant.takeAwayPrice)
    .filter((price): price is number => price != null);

  if (variantPrices.length > 0) return Math.min(...variantPrices);

  return product.deliveryPrice ?? product.takeAwayPrice;
}

/** Whether the listed price is a "from" price: variants priced differently. */
export function hasPriceRange(product: Product): boolean {
  const variantPrices = new Set(
    (product.variants ?? [])
      .map((variant) => variant.deliveryPrice ?? variant.takeAwayPrice)
      .filter((price): price is number => price != null)
  );
  return variantPrices.size > 1;
}

/** The URL segment of a product page: its slug, or its id without one. */
export function getProductHandle(product: Pick<Product, "id" | "slug">) {
  return product.slug || product.id;
}

export function findProductByHandle(
  categories: CatalogCategory[],
  handle: string
): Product | undefined {
  const products = categories.flatMap((category) => category.products);
  return (
    products.find((product) => product.slug === handle) ??
    products.find((product) => product.id === handle)
  );
}

/**
 * Units the customer can still add, given what the cart already holds of the
 * product across all its lines. undefined = unlimited.
 */
export function getRemainingStock(
  product: Pick<Product, "stock">,
  quantityInCart: number
): number | undefined {
  if (product.stock == null) return undefined;
  return Math.max(0, product.stock - quantityInCart);
}

/** Sold out: no stock left at all, or marked unavailable for today. */
export function isSoldOut(
  product: Pick<Product, "stock" | "isAvailableToday">
): boolean {
  return (
    product.isAvailableToday === false ||
    (product.stock != null && product.stock <= 0)
  );
}

/** Whether the product can go straight into the cart from the grid. */
export function canQuickAdd(product: Product): boolean {
  const variantCount = (product.variants ?? []).filter(
    (variant) => (variant.deliveryPrice ?? variant.takeAwayPrice) != null
  ).length;
  const hasRequiredOptions = (product.productOptionSubgroups ?? []).some(
    (subgroup) => Number(subgroup.minOptions ?? 0) >= 1
  );
  return variantCount <= 1 && !hasRequiredOptions && !isSoldOut(product);
}

/** The lowest and highest list price in the catalogue, in whole euros. */
export function getPriceBounds(products: Product[]): {
  min: number;
  max: number;
} {
  const prices = products
    .map(getListPrice)
    .filter((price): price is number => price != null);
  if (prices.length === 0) return { min: 0, max: 0 };
  return {
    min: Math.floor(Math.min(...prices)),
    max: Math.ceil(Math.max(...prices)),
  };
}

/** Number of filters narrowing the catalogue, for the mobile filter button. */
export function countActiveFilters(search: StorefrontSearch): number {
  return (
    (search.category ? 1 : 0) +
    (search.tags?.length ?? 0) +
    (search.min != null || search.max != null ? 1 : 0) +
    (search.inStock ? 1 : 0)
  );
}

function matchesQuery(product: Product, query: string): boolean {
  const needle = query.toLocaleLowerCase();
  return (
    product.name.toLocaleLowerCase().includes(needle) ||
    stripHtml(product.description ?? "")
      .toLocaleLowerCase()
      .includes(needle)
  );
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ");
}

/**
 * The products the catalogue shows for a search: every filter narrows, tags
 * match any of the chosen ones, and the sort runs last. Without a sort the
 * merchant's own order applies: featured products first, then by category and
 * position.
 */
export function filterCatalog(
  categories: CatalogCategory[],
  search: StorefrontSearch
): Product[] {
  const query = search.q?.trim();
  const tags = new Set(search.tags ?? []);

  const products = [...categories]
    .sort((a, b) => a.position - b.position)
    .filter((category) => !search.category || category.id === search.category)
    .flatMap((category) =>
      [...category.products].sort((a, b) => a.position - b.position)
    )
    .filter((product) => !query || matchesQuery(product, query))
    .filter(
      (product) =>
        tags.size === 0 || product.tagIds.some((tagId) => tags.has(tagId))
    )
    .filter((product) => {
      if (search.min == null && search.max == null) return true;
      const price = getListPrice(product);
      if (price == null) return false;
      if (search.min != null && price < search.min) return false;
      if (search.max != null && price > search.max) return false;
      return true;
    })
    .filter((product) => !search.inStock || !isSoldOut(product));

  return sortProducts(products, search.sort ?? StorefrontSorts.featured);
}

function sortProducts(products: Product[], sort: StorefrontSort): Product[] {
  const sorted = [...products];
  switch (sort) {
    case StorefrontSorts.priceAsc:
      return sorted.sort(
        (a, b) =>
          (getListPrice(a) ?? Infinity) - (getListPrice(b) ?? Infinity)
      );
    case StorefrontSorts.priceDesc:
      return sorted.sort(
        (a, b) => (getListPrice(b) ?? -1) - (getListPrice(a) ?? -1)
      );
    case StorefrontSorts.nameAsc:
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case StorefrontSorts.newest:
      return sorted.sort((a, b) =>
        (b.createdAt ?? "").localeCompare(a.createdAt ?? "")
      );
    case StorefrontSorts.featured:
    default:
      // Stable: within each group the category/position order is kept.
      return sorted.sort(
        (a, b) => Number(b.featured) - Number(a.featured)
      );
  }
}
